import type { AdminShopOptionType, SaveShopOptionTypeRequest } from "../../../api/shop";
import { CODE_RULE, isCatalogCode } from "../shared/codes";

const HEX6 = /^#[0-9a-f]{6}$/i;
const HEX3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;

/** A colour as the server stores it: "#abc" (the picker's hex field accepts it) becomes "#aabbcc", lowercase. */
export const normalizeHex = (hex: string): string => {
  const short = HEX3.exec(hex);
  return (short ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}` : hex).toLowerCase();
};

/** A catalog code: 1–6 of A–Z 0–9. The problem to show, or null. */
export const validateCode = (code: string): string | null => (isCatalogCode(code) ? null : `Code: ${CODE_RULE}`);

/** A size or category drawer: its label (or name) and its code. */
export function validateNamed(name: string, code: string, what: "label" | "name"): string | null {
  if (!name.trim()) return `Enter a ${what}`;
  return validateCode(code);
}

/** One value row in the variant-type drawer (new rows have no id). */
export type ValueRow = {
  key: string;
  id?: string;
  label: string;
  code: string;
  /** false: the code still follows the label */
  codeTouched: boolean;
  swatchHex: string;
  productCount: number;
};

export type OptionTypeForm = { name: string; hasSwatch: boolean; values: ValueRow[] };

let valueSeq = 0;
export const newValueRow = (): ValueRow => ({
  key: `new-value-${++valueSeq}`,
  label: "",
  code: "",
  codeTouched: false,
  swatchHex: "",
  productCount: 0,
});

export const valueRowsFrom = (type: AdminShopOptionType): ValueRow[] =>
  [...type.values]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((v) => ({
      key: v.id,
      id: v.id,
      label: v.label,
      code: v.code,
      codeTouched: true,
      swatchHex: v.swatchHex || "",
      productCount: v.productCount,
    }));

export function validateOptionType(form: OptionTypeForm): string | null {
  if (!form.name.trim()) return "Enter a name";
  const labels = new Set<string>();
  const codes = new Set<string>();
  for (const value of form.values) {
    const label = value.label.trim();
    if (!label) return "Every value needs a label";
    if (!isCatalogCode(value.code)) return `Code for ${label}: ${CODE_RULE}`;
    if (labels.has(label.toLowerCase())) return `${label} is listed twice`;
    if (codes.has(value.code)) return `Code ${value.code} is used twice`;
    if (form.hasSwatch && !HEX6.test(normalizeHex(value.swatchHex))) return `Pick a colour for ${label}`;
    labels.add(label.toLowerCase());
    codes.add(value.code);
  }
  return null;
}

/** The save body: values in the drawer's order (that order is saved), swatches only when they are on. */
export const optionTypeRequest = (form: OptionTypeForm): SaveShopOptionTypeRequest => ({
  name: form.name.trim(),
  hasSwatch: form.hasSwatch,
  values: form.values.map((v) => ({
    id: v.id,
    label: v.label.trim(),
    code: v.code,
    swatchHex: form.hasSwatch ? normalizeHex(v.swatchHex) : null,
  })),
});
